import { matchPath } from "react-router";

import { showAlert } from "../../components/general-ui/alert/alert-service";
import { fetchLiveEventBundle } from "../../connection/live-events/event";
import { IRegistrationResponse, MagicLinkCheck, ReRegisterCheck, ValidateRegistration } from "../../connection/registration";
import { BlProfile, BrandliveEvent, Dictionary } from "../../types/working-model";
import { getStorageItem, removeStorageItem, setStorageItem } from "../../utils/local-storage";
import { findFirstIncompleteStep, hasEventAccess } from "../../utils/registration-utils";
import { DebugDefaultRouteMap, DefaultRouteMap } from '../../components/live-event/hooks/use-route-map';
import store from '../main';
import { setFeatureFlags } from "../actions/feature-flags";
import { jwtDecode, sortRegistrationSteps } from "../../utils/utils";
import { streamsLoader as translationService } from "../../i18n/config";
import { reRegister } from "../actions/event/event-actions";


interface PageLoadResponse {
	registrationId: string | null;
	registeredLanguage?: string | null;
	eventBundleError: string;
	playbackUrls: Dictionary;
	secondaryVideos: Dictionary;
	userVerified: boolean;
	registrationStep: number;
	validationFailure: boolean;
	blProfileUser: BlProfile | null;
	blProfileUserToken: string | null;
	shouldRedirectToRegistration: boolean;
	_404: boolean;
	eventBundle?: BrandliveEvent;
	validSessions: (string | number)[];
	paymentRequired: boolean;
	paidSessions?: string[];
	finishedRegistering: boolean;
	verified: boolean;
	firesidesHostSessions: number[];
	userSavedSessions: string[] | null;
	authorizedPages: number[];
	hasIncompleteRegistrationStep?: boolean;
	registered_language?: string;
	leaderboardIsOn: boolean;
}

export async function LoadEventBundle(channelHost: string, eventUrl: string): Promise<{ eventBundle: BrandliveEvent | null, _404: boolean }> {
	try {
		const eventBundle = await fetchLiveEventBundle(channelHost, eventUrl);

		translationService.postEventLoad(eventBundle);
		const initialState = {
			eventBundle: null,
			_404: false
		};

		if (('message' in eventBundle && eventBundle.message === 'Internal Server Error') || 'error' in eventBundle) {
			console.error(eventBundle);
			initialState._404 = true;
			return initialState;
		}

		initialState.eventBundle = { ...eventBundle, ...{ registration_steps: sortRegistrationSteps(eventBundle.registration_steps) } };
		store.dispatch(setFeatureFlags(eventBundle.feature_flags));

		return initialState;
	} catch (e) {
		console.error(e);
		return { _404: true, eventBundle: null };
	}

}

export async function LoadPage(eventBundle: BrandliveEvent): Promise<PageLoadResponse> {
	const initialState: PageLoadResponse = {
		registrationId: null,
		registeredLanguage: null,
		eventBundleError: '',
		playbackUrls: {},
		secondaryVideos: {},
		userVerified: false,
		registrationStep: 0,
		validationFailure: false,
		blProfileUserToken: null,
		blProfileUser: null,
		shouldRedirectToRegistration: false,
		_404: false,
		validSessions: [],
		paymentRequired: false,
		finishedRegistering: false,
		verified: false,
		firesidesHostSessions: [],
		userSavedSessions: null,
		authorizedPages: [],
		hasIncompleteRegistrationStep: false,
		leaderboardIsOn: false
	};

	try {
		const registrationId = getStorageItem(`reg.${eventBundle.uuid}`);
		const blToken = getStorageItem('bl-profile');
		const blProfileUser: BlProfile | null = blToken ? jwtDecode(blToken) as BlProfile : null;
		const queryParams = new URLSearchParams(window.location.search);

		initialState.blProfileUser = blProfileUser as BlProfile;
		initialState.blProfileUserToken = blToken;

		const userCode = getStorageItem(`user-code`);

		//we have a stored registration id for this user for this session
		if (registrationId && eventBundle.registration_on) {
			try {
				//validates that the registration id is valid and hasn't been messed with - if token is not null, send it, otherwise leave it undefined
				const valid = await ValidateRegistration(eventBundle.uuid, registrationId, userCode, blToken ?? undefined);

				// if user saved sessions returned, set in initial state to pass on to redux
				if (valid?.userSavedSessions) {
					initialState.userSavedSessions = valid.userSavedSessions;
				}
				if (valid?.eventBundle) {
					initialState.eventBundle = valid.eventBundle;
				}

				if (valid?.validSessions) {
					initialState.validSessions = valid.validSessions;
				}
				if (valid?.registered_language) {
					initialState.registeredLanguage = valid.registered_language;
				}

				setRegResponseFields(initialState, eventBundle, valid);
				//set registration id
				initialState.registrationId = registrationId;

				initialState.registeredLanguage = valid.registered_language;

				initialState.leaderboardIsOn = valid.leaderboardIsOn;
			} catch (e) {
				console.error(e);
				initialState.validationFailure = true;
				return initialState;
			}
		} else if (eventBundle.playbackUrls) {
			initialState.playbackUrls = eventBundle.playbackUrls;
			initialState.secondaryVideos = eventBundle.secondaryVideos ?? {};
		}

		const reRegisterCode = queryParams.get('reg-code');
		const magicLinkCode = queryParams.get('magic-link');

		let response;
		if (reRegisterCode
			// Magic links do not work when SSO is enabled
			|| (magicLinkCode && !eventBundle.registration_settings?.singleSignOn?.isOn)
		) {
			try {
				//checks the re-register query param and returns the first registration used by user and their token
				//works essentially the same as the registration call
				response = reRegisterCode ?
					await ReRegisterCheck(eventBundle.uuid, reRegisterCode) :
					await MagicLinkCheck(eventBundle.uuid, magicLinkCode as string);

				initialState.blProfileUser = magicLinkCode ? response.blProfile as BlProfile : initialState.blProfileUser;
				setRegResponseFields(initialState, eventBundle, response);

				if ("used" in response) {
					//display alert telling user that the code is used up
					showAlert({
						message: "Registration Link Used",
						description: "Sorry, it looks like that email link has already been used. Please register again or click the 'Already Registered' link.",
						type: "error",
						duration: 10000
					});
					// these two states must be set together - setting redirect to registration without user verified could cause an endless loop
					initialState.userVerified = false;
					initialState.shouldRedirectToRegistration = true;
					return initialState;
				}

				const email = initialState.blProfileUser?.email;
				const lang = initialState.registeredLanguage;
				const ACTION = 'login';
				if (magicLinkCode && email && lang) {
					reRegister(
						{
							eventUuid: eventBundle.uuid,
							email: email,
							lang,
							timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
							action: ACTION, // for recaptcha v3
							hostname: window.location.hostname, // for recaptcha
							isMagicLink: true,
						},
						{},
					);
				}
				// if user had existing saved sessions, set them here
				if (response?.userSavedSessions) {
					initialState.userSavedSessions = response.userSavedSessions;
				}

				// get leaderboardIsOn state from outside publish
				initialState.leaderboardIsOn = response.leaderboardIsOn;

				//drop the code from the url in case they refresh, they shouldn't have to be re-checked.
				window.history.replaceState(undefined, document.title, window.location.href.split("?")[0]);
			} catch (e) {
				initialState.userVerified = false;
				initialState.shouldRedirectToRegistration = true;
			}
		}

		//registration is on and user has never registered
		// and does not have an incomplete registration step
		if (!hasEventAccess(eventBundle, initialState.validSessions, initialState.finishedRegistering, initialState.userVerified, initialState.firesidesHostSessions) &&
			!initialState.hasIncompleteRegistrationStep) {
			//should check a set of pages that should only be accessible post registration
			const match = matchPath(window.location.pathname, {
				path: [
					DebugDefaultRouteMap.Session,
					DefaultRouteMap.Session,
					DebugDefaultRouteMap.Home,
					DefaultRouteMap.Home,
					DebugDefaultRouteMap.Sessions,
					DefaultRouteMap.Sessions,
					DebugDefaultRouteMap.Breakout,
					DefaultRouteMap.Breakout,
					DebugDefaultRouteMap.Fireside,
					DefaultRouteMap.Fireside,
				],
				exact: true
			});

			//only redirect if the current path matches a session page
			initialState.shouldRedirectToRegistration = !!match;

			if (magicLinkCode && response?.error === 'missing-required-field') {
				initialState.shouldRedirectToRegistration = true;
			}
		}

		return initialState;
	} catch (e) {
		console.error(e);
		return initialState;
	}
}

function setRegResponseFields(initialState: PageLoadResponse, eventBundle: BrandliveEvent, registrationResponse: IRegistrationResponse) {
	//validation error - remove registration id
	if ("message" in registrationResponse || "error" in registrationResponse || "used" in registrationResponse) {
		initialState.validationFailure = true;
		removeStorageItem(`reg.${eventBundle.uuid}`);
		return; // Short circuit if there's an error
	}

	if (registrationResponse?.paidSessions) {
		initialState.paidSessions = registrationResponse.paidSessions;
	}

	const registrationSteps = eventBundle?.registration_steps?.filter(step => step.isOn);
	const incompleteRegistrationStep = findFirstIncompleteStep(registrationSteps ?? [], eventBundle?.required_registration_questions, registrationResponse.stepsCompleted);

	initialState.playbackUrls = registrationResponse.playbackUrls;
	initialState.validSessions = registrationResponse.validSessions;
	initialState.paymentRequired = registrationResponse.paymentRequired;
	initialState.finishedRegistering = !registrationResponse.paymentRequired && !incompleteRegistrationStep;
	initialState.userVerified = registrationResponse.userVerified;
	initialState.secondaryVideos = registrationResponse?.secondaryVideos ?? {};
	initialState.firesidesHostSessions = registrationResponse?.firesidesHost ?? [];
	initialState.authorizedPages = registrationResponse?.authorizedPages ?? [];
	initialState.shouldRedirectToRegistration = !!incompleteRegistrationStep;

	//update event bundle with sessions from validate registration
	initialState.eventBundle = {
		...eventBundle,
		sessions: registrationResponse.eventBundle.sessions,
	};

	//set registration id
	initialState.registrationId = registrationResponse.registrationId;
	initialState.registeredLanguage = registrationResponse.registered_language;

	const blToken = registrationResponse.token;
	const blProfileUser: BlProfile | null = blToken ? jwtDecode(blToken) as BlProfile : null;

	initialState.blProfileUser = blProfileUser as BlProfile;
	initialState.blProfileUserToken = blToken;


	// Registration ID already exists, so skip to next step
	if (registrationSteps && registrationSteps.length >= 2) {
		initialState.registrationStep = 1;
	}

	//user is essentially verified entirely by this email
	//and the full event is now loaded
	setStorageItem('bl-profile', registrationResponse.token);
	setStorageItem('Bl-Profile-Token', registrationResponse.token);

	if (incompleteRegistrationStep) { // user has an incomplete registration step. don't set reg token and send to pertinent registration step
		setStorageItem(`incompleteRegistration.${eventBundle.uuid}`, registrationResponse.registrationId);
		removeStorageItem(`reg.${eventBundle.uuid}`);
		initialState.registrationStep = registrationSteps?.findIndex(step => step.type === incompleteRegistrationStep) || 0;
		initialState.hasIncompleteRegistrationStep = true;
	} else {
		removeStorageItem(`incompleteRegistration.${eventBundle.uuid}`);
		setStorageItem(`reg.${eventBundle.uuid}`, registrationResponse.registrationId);
		initialState.hasIncompleteRegistrationStep = false;
	}

	if (registrationResponse.userCode) {
		setStorageItem('user-code', registrationResponse.userCode);
	}
}